import { PAGE_TYPES } from '@dashboard/enums/page-types';
import { elementFieldTypeEnums } from './elementFieldTypes';

const ALL_PAGE_TYPES = Object.values(PAGE_TYPES);

const sameText = (a, b) => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

const sameElementAndLabel = (preset, field) =>
  field.element === preset.field.element && sameText(field.label, preset.field.label);

const toValue = label =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const FIELD_PRESETS = [
  {
    id: 'quantity',
    chipLabel: 'Quantity',
    field: { element: elementFieldTypeEnums.QUANTITY, label: 'Quantity' },
    pageTypes: [PAGE_TYPES.CHECKOUT],
    isOnPage: field => field.element === elementFieldTypeEnums.QUANTITY,
  },
  {
    id: 'name',
    chipLabel: 'Name',
    field: {
      element: elementFieldTypeEnums.TEXT,
      type: 'name',
      label: 'Name',
      placeholder: 'Name',
    },
    isOnPage: field => field.type === 'name',
  },
  {
    id: 'address',
    chipLabel: 'Address',
    field: { element: elementFieldTypeEnums.ADDRESS, label: 'Address', collect: 'billing' },
    isOnPage: field => field.element === elementFieldTypeEnums.ADDRESS,
  },
  {
    id: 'company-name',
    chipLabel: 'Company name',
    field: {
      element: elementFieldTypeEnums.TEXT,
      type: 'company-name',
      label: 'Company name',
      placeholder: 'Company name',
    },
    isOnPage: field => field.type === 'company-name',
  },
  {
    id: 'job-title',
    chipLabel: 'Job title',
    field: { element: elementFieldTypeEnums.TEXT, label: 'Job title', placeholder: 'Job title' },
  },
  {
    id: 'website',
    chipLabel: 'Website',
    field: { element: elementFieldTypeEnums.TEXT, label: 'Website', placeholder: 'Website' },
  },
  {
    id: 'tax-id',
    chipLabel: 'Tax ID',
    field: {
      element: elementFieldTypeEnums.TAX_ID,
      type: 'tax-id',
      label: 'Tax ID',
      placeholder: 'Tax ID',
    },
    isOnPage: field => field.element === elementFieldTypeEnums.TAX_ID,
  },
  {
    id: 'po-number',
    chipLabel: 'PO number',
    field: {
      element: elementFieldTypeEnums.PO_NUMBER,
      type: 'po-number',
      label: 'PO number',
      placeholder: 'PO number',
    },
    isOnPage: field => field.element === elementFieldTypeEnums.PO_NUMBER,
  },
  {
    id: 'billing-email',
    chipLabel: 'Billing email',
    field: {
      element: elementFieldTypeEnums.EMAIL,
      type: 'billing-email',
      label: 'Billing email',
      placeholder: 'Billing email',
    },
    isOnPage: field => field.type === 'billing-email',
  },
  {
    id: 'preferred-delivery-date',
    chipLabel: 'Preferred delivery date',
    field: { element: elementFieldTypeEnums.DATE, label: 'Preferred delivery date' },
  },
  {
    id: 'date-of-birth',
    chipLabel: 'Date of birth',
    field: { element: elementFieldTypeEnums.DATE, label: 'Date of birth' },
  },
  {
    id: 'dietary-requirements',
    chipLabel: 'Dietary requirements',
    field: {
      element: elementFieldTypeEnums.MULTIPLE_CHOICE,
      label: 'Dietary requirements',
      options: [
        'None',
        'Vegetarian',
        'Vegan',
        'Gluten-free',
        'Dairy-free',
        'Halal',
        'Kosher',
        'Other',
      ].map(label => ({ label })),
    },
  },
  {
    id: 'message',
    chipLabel: 'Message',
    field: { element: elementFieldTypeEnums.TEXTAREA, label: 'Message', placeholder: 'Message' },
  },
  {
    id: 'how-did-you-hear',
    chipLabel: 'How did you hear about us?',
    field: {
      element: elementFieldTypeEnums.SELECT,
      label: 'How did you hear about us?',
      options: [
        'Google',
        'Facebook',
        'Instagram',
        'TikTok',
        'YouTube',
        'LinkedIn',
        'X (Twitter)',
        'Friend or colleague',
        'Email or newsletter',
        'Podcast',
        'Other',
      ].map(label => ({ label, value: toValue(label) })),
    },
  },
  {
    id: 'newsletter',
    chipLabel: 'Subscribe to newsletter',
    field: { element: elementFieldTypeEnums.CHECKBOX, label: 'Subscribe to newsletter' },
    // Marketing consent has to be optional (GDPR), so this is the one preset that isn't required.
    optional: true,
  },
  {
    id: 'accept-terms-link',
    chipLabel: 'I accept the [terms & conditions]',
    // Checkout labels render inline HTML, so the link needs no new field setting.
    field: {
      element: elementFieldTypeEnums.CHECKBOX,
      label: 'I accept the <a href="https://google.com" target="_blank">terms & conditions</a>',
    },
  },
];

export function getFieldPresets(pageType) {
  return FIELD_PRESETS.filter(preset => (preset.pageTypes ?? ALL_PAGE_TYPES).includes(pageType));
}

const NAME_TYPES = ['name', 'first-name', 'last-name'];

const GUEST_QUESTION_PRESETS = [
  // Name is asked of every attendee by default; this brings it back once removed.
  {
    ...FIELD_PRESETS.find(({ id }) => id === 'name'),
    isOnPage: field => NAME_TYPES.includes(field.type),
    required: true,
  },
  'job-title',
  'company-name',
  {
    id: 'phone',
    chipLabel: 'Phone',
    field: { element: elementFieldTypeEnums.PHONE, label: 'Phone' },
    isOnPage: field => field.element === elementFieldTypeEnums.PHONE,
  },
  {
    id: 'linkedin-profile',
    chipLabel: 'LinkedIn profile',
    field: {
      element: elementFieldTypeEnums.TEXT,
      label: 'LinkedIn profile',
      placeholder: 'LinkedIn profile',
    },
  },
  'dietary-requirements',
]
  .map(preset =>
    typeof preset === 'string' ? FIELD_PRESETS.find(({ id }) => id === preset) : preset,
  )
  // A guest answers only what the merchant makes required; Name and Email address already are.
  .map(({ required, ...preset }) => ({ ...preset, optional: !required }));

export function getGuestQuestionPresets() {
  return GUEST_QUESTION_PRESETS;
}

export function isPresetOnPage(preset, fields = []) {
  const matches = preset.isOnPage ?? (field => sameElementAndLabel(preset, field));
  return fields.some(field => field && !field.isNew && matches(field));
}

// Preset fields start required; sellers untick Required in the editor.
export function buildPresetField(preset) {
  return {
    ...preset.field,
    ...(preset.field.options && { options: preset.field.options.map(option => ({ ...option })) }),
    required: !preset.optional,
  };
}

export { FIELD_PRESETS };
